import { Layout, SelectOption } from "@visma-netvisor/react-component-library";
import OrderSummary from "../Components/OrderSummary/OrderSummary";
import Header from "../Components/Header";
import OrderConfirmation from "./OrderConfirmation/OrderConfirmation";
import { useContext, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ApolloError, gql, useMutation } from "@apollo/client";
import { t } from "i18next";
import { Packages } from "../globalTypes";
import { getUrlParam } from "../utils";
import { getPriceByPacket, getRangeData } from "../Domain/PriceTable";
import { COMPANY_CREATION_URL, strings } from "../constants";
import { sendTag } from "../analytics";
import { sendGAProductView } from "../googleAnalytics";
import { useToast } from "../context/Toast";
import { getLanguageId } from "../i18";
import { getPacketId, getPacketName } from "../Domain/NetvisorPacket";
import { Buffer } from "buffer";
import { useTranslation } from "react-i18next";
import { PricesContext } from "../pricesContext";
import { logFrontendError } from "./LogFrontendError";
import { OrderCreationError } from "./hooks/useCreateOrder";
import {
  getTradeRegistrationYear,
  isNewCampaignActivated,
} from "../Domain/NewCampaign";
import {
  UpdateLead,
  UpdateLeadVariables,
  UpdateLead_updateLead,
} from "./__generated__/UpdateLead";
import { getTrackingInfo } from "../utils/tracking";

const UPDATE_LEAD = gql`
  mutation UpdateLead($input: UpdateLeadInput!) {
    updateLead(input: $input) {
      __typename
      ... on UpdatedLeadResult {
        lead
      }
    }
  }
`;

export type FormInputs = {
  businessIdentityCode: string;
  companyForm: SelectOption<string> | undefined;
  companyName: string;
  address: string;
  postalCode: string;
  city: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  privacyPolicyAgreementCheckbox: boolean;
  termsOfServiceCheckbox: boolean;
  authorizedRepresentativeCheckbox: boolean;
  newCompanyCampaignOptOut: string;
  survey: SelectOption<string> | undefined;
  surveyOther: string;
};

const Content = () => {
  const [updateLead] = useMutation<UpdateLead, UpdateLeadVariables>(
    UPDATE_LEAD
  );

  const { addToast } = useToast();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();
  const language = i18n.language;
  const languageId = getLanguageId(language);
  const prices = useContext(PricesContext);
  const hasProductViewEventHandled = useRef(false);

  const informationProcessingConsentText =
    t("UserInformation.FirstConfirm1") +
    t("UserInformation.PrivacyStatement") +
    t("UserInformation.FirstConfirm2");

  const form = useForm<FormInputs>({
    mode: "onBlur",
    defaultValues: {
      businessIdentityCode:
        sessionStorage.getItem("businessIdentityCode") ?? "",
      companyForm: undefined,
      companyName: sessionStorage.getItem("companyName") ?? "",
      address: sessionStorage.getItem("address") ?? "",
      postalCode: sessionStorage.getItem("postalCode") ?? "",
      city: sessionStorage.getItem("city") ?? "",
      firstName: sessionStorage.getItem("firstName") ?? "",
      lastName: sessionStorage.getItem("lastName") ?? "",
      email: sessionStorage.getItem("email") ?? "",
      phone: sessionStorage.getItem("phone") ?? "",
      privacyPolicyAgreementCheckbox: false,
      termsOfServiceCheckbox: false,
      authorizedRepresentativeCheckbox: false,
      newCompanyCampaignOptOut: "",
      survey: undefined,
      surveyOther: "",
    },
  });

  const { handleSubmit, formState, watch } = form;
  const { isValid } = formState;

  useEffect(() => {
    if (!prices || hasProductViewEventHandled.current) {
      return;
    }
    
    const campaignActivated = sessionStorage.getItem("newCampaignActivated");
    if (campaignActivated === "true") {
      sessionStorage.removeItem("newCampaignActivated");
    } else {
      sendGAProductView(prices);
    }
    hasProductViewEventHandled.current = true;
    
  }, [prices]);

  const onSubmit: SubmitHandler<FormInputs> = async (input) => {
    setIsLoading(true);

    const currentValues = watch();
    for (const [key] of Object.entries(currentValues)) {
      sessionStorage.removeItem(key);
    }

    sessionStorage.removeItem("companyCreationSuccess");
    sessionStorage.removeItem(strings.ORDER_ID_COOKIE_NAME);
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("reauthAttempted:"))
      .forEach((k) => sessionStorage.removeItem(k));

    const incomeSize: number = Number(getUrlParam("turnover"));
    const orderPrice = getPriceByPacket(prices, getUrlParam("packet"));
    const turnoverCategory = getRangeData(incomeSize)?.nvFormat ?? "";

    const leadId = sessionStorage.getItem("leadId") ?? "";

    // keepalive lets the enrichment PATCH outlive authenticate()'s form.submit() navigation; fire-and-forget so the redirect never waits on a slow (8-13s) Salesforce update. Onboarding needs only the form inputs + leadId.
    updateLead({
      variables: {
        input: {
          id: leadId,
          selectedPackage: getPacketName()
            .toString()
            .toUpperCase() as Packages,
          turnoverCategory: turnoverCategory,
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          phoneNumber: input.phone,
          companyName: input.companyName,
          companyStreetAddress: input.address,
          companyCity: input.city,
          companyForm: input.companyForm?.label as string,
          informationProcessingConsentText: informationProcessingConsentText,
          cmrr: orderPrice,
          landingPage: getTrackingInfo(strings.TRACKING_LANDINGPAGE),
          organicSource: getTrackingInfo(strings.TRACKING_ORGANICSOURCE).substring(0, 250),
          organicSourceName: getTrackingInfo(
            strings.TRACKING_ORGANICSOURCENAME
          ),
          trafficSource: getTrackingInfo(strings.TRACKING_TRAFFICSOURCE),
          firstTrafficSource: getTrackingInfo(
            strings.TRACKING_FIRSTTRAFFICSOURCE
          ),
          utmCampaign: getTrackingInfo(strings.TRACKING_UTMCAMPAIGN),
          utmTerm: getTrackingInfo(strings.TRACKING_UTMTERM),
          utmMedium: getTrackingInfo(strings.TRACKING_UTMMEDIUM),
          utmSource: getTrackingInfo(strings.TRACKING_UTMSOURCE),
          utmContent: getTrackingInfo(strings.TRACKING_UTMCONTENT),
          survey: input.survey?.value ?? "",
          surveyOther: input.surveyOther,
          isNewCompanyCampaign: isNewCampaignActivated(),
          newCompanyCampaignOptOut: input.newCompanyCampaignOptOut === '1'
        },
      },
      context: { fetchOptions: { keepalive: true } },
    })
      .then((res) =>
        res.data?.updateLead != null && isSuccessResponse(res.data.updateLead)
          ? undefined
          : logFrontendError({
              info: "Lead enrichment unsuccessful (non-blocking)",
              level: "warning",
              organizationIdentifier: input.businessIdentityCode,
              details: { reason: "unsuccessResponse" },
            })
      )
      .catch((error) =>
        logFrontendError({
          info: "Lead enrichment failed (non-blocking)",
          level: "warning",
          error,
          organizationIdentifier: input.businessIdentityCode,
          details: {
            reason: "error",
            graphQLErrorCodes:
              error instanceof ApolloError
                ? error.graphQLErrors
                    .map((e) => e.extensions?.code)
                    .filter((c): c is string => typeof c === "string")
                : undefined,
          },
        })
      );

    sendTag({ status: "validFormSent" });
    addToast("Tilaus vahvistettu", "success");
    authenticate(input, leadId, languageId);
  };

  return (
    <>
      <Layout.Flex
        flexDirection="column"
        flexGap={0}
        styleOverrides={{
          minHeight: "120vh",
        }}
      >
        <Header />

        <FormProvider {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isValid) {
                handleSubmit(onSubmit)(e).catch(async (error) => {
                  const isApollo = error instanceof ApolloError;
                  const networkError = isApollo ? error.networkError : undefined;
                  const networkStatusCode =
                    networkError &&
                    "statusCode" in networkError &&
                    typeof networkError.statusCode === "number"
                      ? networkError.statusCode
                      : undefined;
                  const graphQLErrorCodes = isApollo
                    ? error.graphQLErrors
                        .map((e) => e.extensions?.code)
                        .filter((c): c is string => typeof c === "string")
                    : undefined;
                  await logFrontendError({
                    info: "Order submit failed",
                    status: error instanceof OrderCreationError ? error.status : undefined,
                    error,
                    details: {
                      networkStatusCode,
                      graphQLErrorCodes,
                    },
                  });
                  setError("Submit failed");
                });
              }
            }}
          >
            <StyledMainFlex>
              <StyledLayoutFlex>
                <Layout.Item columnsMd="span 7" className="order-confirmation">
                  <OrderConfirmation
                    error={error}
                    setError={setError}
                    isLoading={isLoading}
                  />
                </Layout.Item>
                <Layout.Item columnsMd="span 5" className="order-summary">
                  <OrderSummary />
                </Layout.Item>
              </StyledLayoutFlex>
            </StyledMainFlex>
          </form>
        </FormProvider>
      </Layout.Flex>
    </>
  );
};

export default Content;

function authenticate(
  formInputs: FormInputs,
  leadId: string,
  languageId: number
) {
  const incomeSize: number = Number(getUrlParam("turnover"));
  const turnoverGroupIdentifier = getRangeData(incomeSize)?.nvPacketIdentifier;

  const data = {
    SelectedPacketIdentifier: getPacketId(),
    TurnoverGroupIdentifier: turnoverGroupIdentifier,
    FirstName: formInputs.firstName,
    LastName: formInputs.lastName,
    Email: formInputs.email,
    PhoneNumber: formInputs.phone,
    OrganizationIdentifier: formInputs.businessIdentityCode,
    CompanyName: formInputs.companyName,
    Address: formInputs.address,
    ZipCode: formInputs.postalCode,
    City: formInputs.city,
    AccountListTypeIdentifier: formInputs.companyForm?.value,
    LeadId: leadId,
    LanguageId: languageId,
    TradeRegistrationYear: getTradeRegistrationYear(),
    IsNewCompanyCampaign: isNewCampaignActivated(),
  };

  const encodedData = Buffer.from(JSON.stringify(data), "utf8").toString(
    "base64"
  );

  sessionStorage.setItem(
    strings.ORDER_DATA_COOKIE_NAME,
    JSON.stringify({ data: encodedData })
  );

  const form = document.createElement("form");

  form.method = "POST";
  form.action = `${COMPANY_CREATION_URL}/CompanyCreation`;

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "data";
  input.value = encodedData;
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
}

function isSuccessResponse(
  response: UpdateLead_updateLead
): response is UpdateLead_updateLead {
  return response.__typename === "UpdatedLeadResult";
}

const StyledLayoutFlex = styled(Layout.Flex)`
  display: flex;
  max-width: 1146px;
  gap: 64px;
  overflow: auto;

  .order-confirmation {
    order: 1;
    flex: 0 0 600px;
    min-width: 0;
  }

  .order-summary {
    order: 2;
    flex: 0 0 482px;
    min-width: 0;
    overflow: hidden;
  }

  @media (max-width: 1218px) {
    .order-confirmation {
      flex: 10 1 0;
    }

    .order-summary {
      flex: 8 1 0;
    }
  }

  @media (max-width: 768px) {
    display: inline-grid;
    gap: 16px;

    .order-confirmation {
      order: 2;
    }

    .order-summary {
      order: 1;
      overflow: hidden;
    }
  }
`;
const StyledMainFlex = styled(Layout.Flex)`
  as: main;
  justify-content: center;
  background-color: #ffffff;
  flex-grow: 1;
  padding: 48px 28px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
